import { jsPDF } from "jspdf";

export function generateCnFInvoicePdf(orderId, order, hsnSummary, totals, seller) {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Helper to draw a box
    const drawBox = (x, y, w, h) => {
        pdf.setLineWidth(0.2);
        pdf.rect(x, y, w, h);
    };

    // Helper for multi-line text in a box
    const drawWrappedText = (text, x, y, maxWidth, lineHeight = 4) => {
        const lines = pdf.splitTextToSize(text || "", maxWidth);
        lines.forEach((line, i) => {
            pdf.text(line, x, y + (i * lineHeight));
        });
        return lines.length * lineHeight;
    };

    // 1. Initial Header
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("Tax Invoice", pageWidth / 2, 10, { align: "center" });

    // Header Grid
    // Top Row: Seller and Invoice Info
    let currentY = 15;
    const leftColW = (pageWidth - 20) * 0.55;
    const rightColW = (pageWidth - 20) * 0.45;

    // Seller Box (Left)
    drawBox(10, currentY, leftColW, 45);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text(seller.name, 12, currentY + 5);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    let sellerY = currentY + 10;
    sellerY += drawWrappedText(seller.address1, 12, sellerY, leftColW - 5);
    sellerY += drawWrappedText(seller.address2, 12, sellerY, leftColW - 5);
    sellerY += drawWrappedText(seller.address3, 12, sellerY, leftColW - 5);
    pdf.text(`GSTIN/UIN: ${seller.gstin}`, 12, sellerY + 2);
    pdf.text(`State Name : ${seller.state}, Code : ${seller.stateCode}`, 12, sellerY + 6);

    // Invoice Info Box (Right)
    drawBox(10 + leftColW, currentY, rightColW, 45);
    pdf.setFont("helvetica", "normal");
    pdf.text("Invoice No.", 10 + leftColW + 2, currentY + 5);
    pdf.setFont("helvetica", "bold");
    pdf.text(orderId, 10 + leftColW + 2, currentY + 9);
    
    pdf.line(10 + leftColW, currentY + 12, 10 + leftColW + rightColW, currentY + 12);
    pdf.setFont("helvetica", "normal");
    pdf.text("Dated", 10 + leftColW + 2, currentY + 16);
    pdf.setFont("helvetica", "bold");
    
    // Use approvedDateOnly if available, fallback to generation date
    const displayDate = order.approvedDateOnly 
        ? new Date(order.approvedDateOnly).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })
        : new Date().toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' });
        
    pdf.text(displayDate, 10 + leftColW + 2, currentY + 20);

    pdf.line(10 + leftColW, currentY + 23, 10 + leftColW + rightColW, currentY + 23);
    pdf.setFont("helvetica", "normal");
    pdf.text("Destination", 10 + leftColW + 2, currentY + 27);
    pdf.setFont("helvetica", "bold");
    pdf.text(order.place || order.place || "-", 10 + leftColW + 2, currentY + 31);

    // Consignee Box
    currentY += 45;
    drawBox(10, currentY, leftColW, 35);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.text("Consignee (Ship to)", 12, currentY + 4);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(order.partyName || order.name || "", 12, currentY + 9);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    let consigneeY = currentY + 13;
    consigneeY += drawWrappedText(order.address || "", 12, consigneeY, leftColW - 5);
    pdf.text(`GSTIN/UIN: ${order.gstno || "URP"}`, 12, consigneeY + 2);
    pdf.text(`State Name: ${order.state || "Karnataka"}, Code: ${order.stateCode || "29"}`, 12, consigneeY + 6);

    // Buyer Box
    drawBox(10 + leftColW, currentY, rightColW, 35);
    pdf.setFontSize(8);
    pdf.text("Buyer (Bill to)", 10 + leftColW + 2, currentY + 4);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(order.partyName || order.name || "", 10 + leftColW + 2, currentY + 9);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    let buyerY = currentY + 13;
    buyerY += drawWrappedText(order.address || "", 10 + leftColW + 2, buyerY, rightColW - 5);
    pdf.text(`GSTIN/UIN: ${order.gstno || "URP"}`, 10 + leftColW + 2, buyerY + 2);
    pdf.text(`State Name: ${order.state || "Karnataka"}, Code: ${order.stateCode || "29"}`, 10 + leftColW + 2, buyerY + 6);

    // Item Table
    currentY += 35;
    const tableH = 80;
    drawBox(10, currentY, pageWidth - 20, tableH);
    
    // Headers
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("Sl No.", 12, currentY + 5);
    pdf.text("Description of Goods", 20, currentY + 5);
    pdf.text("HSN", 87, currentY + 5);
    pdf.text("Quantity", 123, currentY + 5, { align: "right" });
    pdf.text("Rate", 143, currentY + 5, { align: "right" });
    pdf.text("GST 5%", 168, currentY + 5, { align: "right" });
    pdf.text("Amount", pageWidth - 12, currentY + 5, { align: "right" });
    pdf.line(10, currentY + 7, pageWidth - 10, currentY + 7);

    // Vertical lines for table
    pdf.line(18, currentY, 18, currentY + tableH);
    pdf.line(85, currentY, 85, currentY + tableH);
    pdf.line(105, currentY, 105, currentY + tableH);
    pdf.line(125, currentY, 125, currentY + tableH);
    pdf.line(145, currentY, 145, currentY + tableH);
    pdf.line(170, currentY, 170, currentY + tableH);

    // Table Content
    let itemY = currentY + 12;
    pdf.setFont("helvetica", "normal");
    let totalQty = 0;
    
    Object.entries(hsnSummary).forEach(([hsn, data], index) => {
        pdf.text(String(index + 1), 12, itemY);
        pdf.text(data.name || data.description || "PAN MASALA", 20, itemY);
        
        // Show Breakdown if available
        if (data.breakdown && data.breakdown.length > 0) {
            pdf.setFontSize(6.5);
            pdf.setFont("helvetica", "italic");
            let bY = itemY + 4;
            data.breakdown.forEach(b => {
                pdf.text(b, 20, bY);
                bY += 3.5;
            });
            pdf.setFontSize(8);
            pdf.setFont("helvetica", "normal");
        }

        const lineAmount = data.basic + (data.gstAmt || 0);

        pdf.text(hsn, 87, itemY);
        pdf.text(data.qty.toFixed(2) + " BAG", 123, itemY, { align: "right" });
        const avgRate = data.basic / (data.qty || 1);
        pdf.text(avgRate.toFixed(2), 143, itemY, { align: "right" });
        pdf.text((data.gstAmt || 0).toFixed(2), 168, itemY, { align: "right" });
        pdf.text(lineAmount.toFixed(2), pageWidth - 12, itemY, { align: "right" });
        
        // Calculate next itemY based on breakdown length
        const bheight = data.breakdown ? data.breakdown.length * 4 : 0;
        itemY += Math.max(8, bheight + 5);
        totalQty += data.qty;
    });

    // Total Row
    pdf.setFont("helvetica", "bold");
    const totalRowY = currentY + tableH - 5;
    pdf.line(10, totalRowY - 2, pageWidth - 10, totalRowY - 2);
    pdf.text("Total", 20, totalRowY);
    pdf.text(totalQty.toFixed(2) + " BAG", 123, totalRowY, { align: "right" });
    pdf.text(totals.tGrand.toFixed(2), pageWidth - 12, totalRowY, { align: "right" });

    // Amount in Words
    currentY += tableH;
    drawBox(10, currentY, pageWidth - 20, 10);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.text("Amount Chargeable (in words)", 12, currentY + 4);
    pdf.setFont("helvetica", "bold");
    pdf.text(`INR ${numberToEnglish(Math.round(totals.tGrand))} Only`, 12, currentY + 8);

    // GST Registry
    currentY += 10;
    const gstH = 25;
    drawBox(10, currentY, pageWidth - 20, gstH);
    pdf.setFont("helvetica", "bold");
    pdf.text("HSN/SAC", 12, currentY + 5);
    pdf.text("Taxable Value", 60, currentY + 5, { align: "right" });
    pdf.text("Central Tax", 95, currentY + 5, { align: "right" });
    pdf.text("State Tax", 130, currentY + 5, { align: "right" });
    pdf.text("Total Tax Amount", pageWidth - 12, currentY + 5, { align: "right" });
    pdf.line(10, currentY + 7, pageWidth - 10, currentY + 7);

    // GST Vertical lines
    pdf.line(40, currentY, 40, currentY + gstH);
    pdf.line(75, currentY, 75, currentY + gstH);
    pdf.line(110, currentY, 110, currentY + gstH);
    pdf.line(145, currentY, 145, currentY + gstH);

    pdf.setFont("helvetica", "normal");
    let gstY = currentY + 11;
    let totalTax = 0;
    Object.entries(hsnSummary).forEach(([hsn, data]) => {
        pdf.text(hsn, 12, gstY);
        pdf.text(data.basic.toFixed(2), 60, gstY, { align: "right" });
        pdf.text((data.gstAmt / 2).toFixed(2), 95, gstY, { align: "right" });
        pdf.text((data.gstAmt / 2).toFixed(2), 130, gstY, { align: "right" });
        pdf.text(data.gstAmt.toFixed(2), pageWidth - 12, gstY, { align: "right" });
        gstY += 5;
        totalTax += data.gstAmt;
    });

    // Tax amount in words
    currentY += gstH;
    drawBox(10, currentY, pageWidth - 20, 8);
    pdf.text(`Tax Amount (in words) : INR ${numberToEnglish(Math.round(totalTax))} Only`, 12, currentY + 5);

    // Footer
    currentY += 8;
    drawBox(10, currentY, pageWidth - 20, 30);
    pdf.setFontSize(7);
    
    // Build Unit Config Remarks
    const configStr = order.unitConfigs ? Object.values(order.unitConfigs).join("; ") : "";
    pdf.setFont("helvetica", "bold");
    pdf.text("Remarks:", 12, currentY + 5);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Being Sale of ${totals.tQty.toFixed(2)} Bags ( ${configStr} )`, 25, currentY + 5);
    
    pdf.text("Declaration:", 12, currentY + 15);
    pdf.text("We declare that this invoice shows the actual price of the", 12, currentY + 19);
    pdf.text("goods described and that all particulars are true and correct.", 12, currentY + 22);

    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    pdf.text(`for ${seller.name}`, pageWidth - 15, currentY + 5, { align: "right" });
    pdf.text("Authorized Signatory", pageWidth - 15, currentY + 25, { align: "right" });

    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.text("This is a Computer Generated Invoice", pageWidth / 2, currentY + 35, { align: "center" });

    return pdf;
}

function numberToEnglish(n) {
    const string = n.toString();
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const scales = ['', 'Thousand', 'Lakh', 'Crore'];

    if (n === 0) return 'Zero';

    const splitIntoChunks = (str) => {
        const chunks = [];
        // Indian system: units(3), then 2, 2, 2...
        if (str.length > 3) {
            chunks.push(str.slice(-3));
            let remaining = str.slice(0, -3);
            while (remaining.length > 0) {
                chunks.push(remaining.slice(-2));
                remaining = remaining.slice(0, -2);
            }
        } else {
            chunks.push(str);
        }
        return chunks;
    };

    const formatChunk = (chunk) => {
        let res = '';
        const num = parseInt(chunk);
        if (num >= 100) {
            res += units[Math.floor(num / 100)] + ' Hundred ';
        }
        const lastTwo = num % 100;
        if (lastTwo < 20) {
            res += units[lastTwo];
        } else {
            res += tens[Math.floor(lastTwo / 10)] + ' ' + units[lastTwo % 10];
        }
        return res.trim();
    };

    const chunks = splitIntoChunks(string);
    let words = [];
    for (let i = 0; i < chunks.length; i++) {
        const chunkWords = formatChunk(chunks[i]);
        if (chunkWords) {
            words.push(chunkWords + ' ' + scales[i]);
        }
    }
    return words.reverse().join(' ').trim();
}
